/*
 * Beptum: přidání řádků do košíku (/cart/add.js) šetrně k ochraně Shopify.
 *
 * Čerstvě založené dynamické varianty (beptum-admin /api/…/cart-variants)
 * storefront pár sekund nezná a /cart/add.js na ně vrací 422 „ausverkauft".
 * Dřív se to řešilo opakovaným POST až 8× — Shopify ale opakované neúspěšné
 * přidávání bere jako zneužití a návštěvníkovi na čas zablokuje přidávání
 * do košíku úplně („Zu viele Versuche", hlášeno 25. 9. 2026).
 *
 * Proto: nejdřív GET /variants/{id}.js (do limitu přidávání se nepočítá),
 * POST až když jsou varianty dostupné, nejvýš 2 pokusy. Blokaci rozpozná
 * a vrátí `throttled: true` + srozumitelnou hlášku (DE/EN).
 *
 * Použití (konfigurátory, Meine Pläne, Aufmaß-Portal):
 *   const res = await window.bpCartAdd(items, { addUrl: '/cart/add' });
 *   if (res.throttled) → zobrazit res.message; jinak res.ok / res.status / res.data
 */
(function () {
  'use strict';

  // Čekání na dostupnost variant: ~12 s celkem, zpočátku hustěji.
  var POLL_MS = [300, 500, 700, 900, 1200, 1500, 2000, 2500, 3000];
  var CONCURRENCY = 4;
  var RETRY_WAIT_MS = 2500;

  var MSG = {
    de: 'Der Warenkorb ist vorübergehend gesperrt, weil in kurzer Zeit zu viele Artikel hinzugefügt wurden. Bitte warten Sie einige Minuten und versuchen Sie es dann erneut.',
    en: 'The cart is temporarily locked because too many items were added in a short time. Please wait a few minutes and try again.'
  };

  function lang() {
    var l = String(window.bpLocale || document.documentElement.lang || 'de').slice(0, 2).toLowerCase();
    return l === 'en' ? 'en' : 'de';
  }

  function sleep(ms) {
    return new Promise(function (r) { setTimeout(r, ms); });
  }

  function isThrottled(status, data) {
    if (status === 429) return true;
    var text = String((data && (data.description || data.message)) || '');
    return /zu viele versuche|too many (attempts|requests)/i.test(text);
  }

  function variantReady(id) {
    return fetch('/variants/' + encodeURIComponent(id) + '.js', {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    }).then(function (r) {
      if (!r.ok) return false;
      return r.json().then(function (v) { return !!(v && v.available); }, function () { return false; });
    }).catch(function () { return false; });
  }

  /** Vrátí id, která ještě nejsou dostupná (max CONCURRENCY dotazů najednou). */
  async function notReady(ids) {
    var pending = [];
    var i = 0;
    async function worker() {
      while (i < ids.length) {
        var id = ids[i++];
        if (!(await variantReady(id))) pending.push(id);
      }
    }
    var workers = [];
    for (var k = 0; k < Math.min(CONCURRENCY, ids.length); k++) workers.push(worker());
    await Promise.all(workers);
    return pending;
  }

  /** Počká, až storefront zná všechny varianty. Co nestihne, zkusí se přidat i tak. */
  async function waitForVariants(ids) {
    var pending = ids.slice();
    for (var a = 0; pending.length && a <= POLL_MS.length; a++) {
      pending = await notReady(pending);
      if (pending.length && a < POLL_MS.length) await sleep(POLL_MS[a]);
    }
    if (pending.length) console.warn('[bp] Varianty se neobjevily ve storefrontu včas:', pending);
    return pending;
  }

  function post(url, items) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      return r.json().catch(function () { return {}; }).then(function (data) {
        return { ok: r.ok, status: r.status, data: data };
      });
    });
  }

  function result(res) {
    res.throttled = isThrottled(res.status, res.data);
    res.message = res.throttled
      ? MSG[lang()]
      : String((res.data && (res.data.description || res.data.message)) || res.status);
    return res;
  }

  async function bpCartAdd(items, opts) {
    opts = opts || {};
    var url = opts.addUrl || '/cart/add';
    if (!/\.js$/.test(url)) url += '.js';

    var ids = [];
    (items || []).forEach(function (it) {
      var id = String(it && it.id || '');
      if (id && ids.indexOf(id) < 0) ids.push(id);
    });

    await waitForVariants(ids);
    var res = result(await post(url, items));

    // Jeden opakovaný pokus jen na „ausverkauft" (varianta pořád není vidět), nikdy na blokaci.
    if (res.status === 422 && !res.throttled) {
      await sleep(RETRY_WAIT_MS);
      await waitForVariants(ids);
      res = result(await post(url, items));
    }
    if (res.throttled) console.warn('[bp] Shopify dočasně blokuje přidávání do košíku:', res.data);
    return res;
  }

  bpCartAdd.throttledMessage = function () { return MSG[lang()]; };
  bpCartAdd.isThrottled = isThrottled;
  window.bpCartAdd = bpCartAdd;
})();
